import Hash from 'object-hash'
import { i18n } from '@/i18n'
import { path, mergeDeepRight, assocPath } from 'ramda'
import StepCustomField from '@/views/Project/Mutate/Steps/CustomFields.vue'
import { Query } from 'mingo'

const Clone = function () {
  return JSON.parse(JSON.stringify.apply(this, arguments))
}

const getButtonThemes = (item = {}, type = 'project', options = {}) => {
  const { source = '' } = options
  const defaultItems = {
    project: [
      {
        value: 'primary',
        label: 'primaryColor',
        style: {
          background: 'themeColor',
        },
      },
      {
        value: 'secondary',
        label: 'secondaryColor',
        style: {
          background: 'themeColorSecondary',
        },
      },
    ],
    page: [
      {
        value: 'primary',
        label: 'primaryColor',
        style: {
          background: 'theme.primary',
        },
      },
      {
        value: 'secondary',
        label: 'secondaryColor',
        style: {
          background: 'theme.secondary',
        },
      },
    ],
  }

  const data = source ? path(source, item) : item

  function getContrastColor(color) {
    if (!color) return
    const hex = color.replace('#', '')
    const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.substring(i, i + 2), 16))
    const brightness = (r * 299 + g * 587 + b * 114) / 1000
    if (type !== 'page') return brightness < 155 ? 'white' : 'black'
    if (brightness < 140) return 'white'

    const [rn, gn, bn] = [r, g, b].map(c => c / 255)
    const max = Math.max(rn, gn, bn)
    const min = Math.min(rn, gn, bn)
    const delta = max - min
    let h = 0
    if (delta) {
      if (max === rn) h = ((gn - bn) / delta) % 6
      else if (max === gn) h = (bn - rn) / delta + 2
      else h = (rn - gn) / delta + 4
    }
    h = Math.round(h * 60)
    if (h < 0) h += 360
    const l = (max + min) / 2
    const s = delta ? delta / (1 - Math.abs(2 * l - 1)) : 0

    return `hsl(${h}, ${+(s * 100).toFixed(1)}%, 20%)`
  }

  // Page keeps its settings on `theme.settings`, Project on the top level `themeSettings`
  const customThemes =
    path(['theme', 'settings', 'buttonThemes'], data) || path(['themeSettings', 'buttonThemes'], data) || []

  const defaults = defaultItems[type] || defaultItems.project
  const defaultValues = defaults.map(theme => theme.value)
  const themes = [
    ...defaults.map(theme => customThemes.find(custom => custom.value === theme.value) || theme),
    ...customThemes.filter(theme => !defaultValues.includes(theme.value)),
  ]

  // a theme can declare paths that all need a value on the item before it is offered
  const visibleThemes = themes.filter(theme =>
    ToArray(theme.condition).every(valuePath => path(valuePath.split('.'), data)),
  )

  function toAlpha(color, alpha) {
    const hex = color.replace('#', '')
    const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.substring(i, i + 2), 16))
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  function resolveColor(spec = '') {
    const { path: valuePaths = '', fallback } = typeof spec === 'string' ? { path: spec } : spec
    const value = ToArray(valuePaths)
      .map(valuePath => path(valuePath.split('.'), data))
      .find(Boolean)
    return value || fallback
  }
  function resolveStyle(spec) {
    // a fallback can be a spec of its own, e.g. the contrast of another color
    if (spec && typeof spec.fallback === 'object') {
      const { fallback, ...rest } = spec
      return resolveStyle(rest) || resolveStyle(fallback)
    }

    let value = resolveColor(spec)
    if (!value || typeof spec === 'string') return value
    if (spec.contrast) value = getContrastColor(value)
    if (spec.alpha !== undefined) value = toAlpha(value, spec.alpha)
    return spec.template ? spec.template.replace('{value}', value) : value
  }

  return visibleThemes.map(theme => {
    const styleObj = theme.style || {}

    const declarations = Object.entries(styleObj)
      .map(([prop, spec]) => {
        const value = resolveStyle(spec)
        return value ? `${prop}: ${value};` : ''
      })
      .filter(Boolean)

    if (!('color' in styleObj) || !resolveStyle(styleObj.color)) {
      const background = resolveColor(styleObj.background)
      const contrast = getContrastColor(background)
      if (contrast) declarations.push(`color: ${contrast};`)
    }

    return {
      ...theme,
      style: declarations.join(' '),
    }
  })
}

// a model template keeps the page/project it creates under `data`, but its custom fields and theme settings on the top level
const getModelTemplateThemeItem = (template = {}) => ({
  ...(template.data || {}),
  customFields: template.customFields,
  themeSettings: template.themeSettings,
})

const isObject = function (val) {
  return Object.prototype.toString.call(val) === '[object Object]'
}

const SortByKey =
  (property, options = {}) =>
  (a, b) => {
    const { preferredOrder = [], direction = 'ASC' } = options

    const d = { ASC: -1, DESC: 1 }

    const aValue = a[property]
    const bValue = b[property]

    // Check if values are in preferredOrder
    const aIndex = preferredOrder.indexOf(aValue)
    const bIndex = preferredOrder.indexOf(bValue)

    // If both values are in preferredOrder, sort by their order
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex
    }
    // If only one value is in preferredOrder, prioritize it
    if (aIndex !== -1) return d[direction]
    if (bIndex !== -1) return d[direction] * -1

    // If neither value is in preferredOrder, sort naturally
    if (aValue < bValue) return d[direction]
    if (aValue > bValue) return d[direction] * -1
    return 0
  }

const ReduceArrayUniqueByKey = key => {
  key = key instanceof Array ? key : key.split('.')
  return (res, cur) => {
    if (!res.find(r => path(key, r) === path(key, cur))) res.push(cur)
    return res
  }
}

function PartialUpdate(result, replacer, options = {}) {
  if (!result) result = {}
  const { strict, strategy, arrayId = 'id' } = options

  const isRelationalArray = a => a.length && a.every(i => isObject(i) && arrayId in i)
  const isArrayIdEqual = (a, b) => a[arrayId].split(':').pop() === b[arrayId].split(':').pop()
  const canWrite = (key, target, value) => {
    if (strict && !(key in target)) return false // Prevent property adding
    if (strict && isObject(target[key]) && [null, undefined].includes(value[key])) return false // Prevent object overrides

    return true
  }

  Object.entries(replacer)
    .filter(([key]) => canWrite(key, result, replacer))
    .forEach(([key, value]) => {
      if (result[key] && isObject(value)) return (result[key] = PartialUpdate(result[key], value, options))
      if (strategy === 'diff' && value instanceof Array && isRelationalArray(value)) {
        result[key] = result[key]
          .filter(a => !value.find(b => b[arrayId] === `Delete:${a[arrayId]}`))
          .map(a => {
            let match = value.find(b => isArrayIdEqual(a, b))
            if (!match) return a

            let { [arrayId]: id, ...rest } = match

            return PartialUpdate(a, rest, options)
          })

        result[key].push(
          ...value
            .filter(v => !v[arrayId].startsWith('Delete:') && !result[key].find(r => isArrayIdEqual(r, v)))
            .map(v => ({ ...v, [arrayId]: v[arrayId].split(':').pop() })),
        )

        let sortPattern = /^Sort\[(\d+)\]:(.+)/
        value
          .map(a => {
            let match = a[arrayId].match(sortPattern)
            if (!match) return
            let [to, id] = match.slice(1)
            to = parseInt(to)
            return { to, id }
          })
          .filter(Boolean)
          .sort((a, b) => a.to - b.to)
          .forEach(({ id, to }) => {
            let from = result[key].findIndex(e => e[arrayId] === id)
            let item = result[key].splice(from, 1)[0]
            result[key].splice(to, 0, item)
          })

        return result[key]
      }

      return (result[key] = value)
    })

  return result
}

function IconBinding(value) {
  if (!value) return
  if (value instanceof Array || typeof value === 'string') return { icon: value }
  return value
}

function setByPath(path = '', value, obj, original = obj) {
  if (!obj) return

  let getValue = (v, s) => {
    if (typeof v === 'function') return v(s, original)
    return v
  }

  if (typeof path === 'string') path = path.split('.')
  let current = path.shift()

  // Handle wildcards
  if (path[0] === '*' && obj[current] instanceof Array) {
    path = path.slice(1) // Remove wildcard from path
    if (!path.length) return obj[current].map(entry => getValue(value, entry))

    return obj[current].map(entry => setByPath([...path], value, entry, original))
  }

  if (path.length) return setByPath(path, value, obj[current], original)
  return (obj[current] = getValue(value, obj[current]))
}

function getByPath(path, obj) {
  if (!obj) return

  if (typeof path === 'string') path = path.split('.')
  let current = path.shift()

  if (!(current in obj)) return

  if (path.length) return getByPath(path, obj[current])
  return obj[current]
}

function Debounce(func, wait, immediate) {
  let timeout
  return function () {
    let context = this,
      args = arguments
    let later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    let callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

const LoadImage = source =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (source instanceof Blob) URL.revokeObjectURL(source)
      resolve(img)
    }
    img.onerror = reject
    img.src = source instanceof Blob ? URL.createObjectURL(source) : source
  })

const getDimensions = url => LoadImage(url).then(({ width, height }) => ({ width, height }))

function getCookie(cname) {
  var name = cname + '='
  var decodedCookie = decodeURIComponent(document.cookie)
  var ca = decodedCookie.split(';')
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i]
    while (c.charAt(0) == ' ') {
      c = c.substring(1)
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length)
    }
  }
  return ''
}

function Capitalize(val = '') {
  return val.charAt(0).toUpperCase() + val.substring(1)
}

function Initials(val = '') {
  return val
    .split(' ')
    .map(p => p.substring(0, 1))
    .join('')
}

function ValueComp() {
  return {
    get() {
      return this.value
    },
    set(val) {
      this.$emit('input', val)
    },
  }
}

function CamelCase(value) {
  return value.replace(/-([a-z])/g, function (g) {
    return g[1].toUpperCase()
  })
}

function Wait(ms) {
  return new Promise((resolve, reject) => {
    setTimeout(resolve, ms)
  })
}

async function WaitUntil(func, options = {}) {
  let { interval = 200, limit = 50 } = options
  let count = 0
  let result = false

  while (count < limit) {
    count++
    if (func()) break
    await Wait(interval)
  }

  if (count >= limit) throw new Error('Could not resolve')
  return true
}

function ResidentialNumberPatternParser(pattern, input = '', replaceChar = '0') {
  if (input.toString) input = input.toString()

  let inputChars = input.split('')

  return pattern
    .split('')
    .map(char => {
      if (char !== replaceChar) return char

      if (!inputChars.length) return ''
      return inputChars.shift()
    })
    .join('')
}

function RemoveByValue(target, value, options = {}) {
  let { deep = false } = options
  let values = value instanceof Array ? value : [value]

  target = Clone(target)

  Object.keys(target).forEach(key => {
    if (values.includes(target[key])) delete target[key]
    if (isObject(target[key]) && deep) target[key] = RemoveByValue(target[key], values, options)
  })

  return target
}

async function Run(action, options = {}) {
  let { retryCount = 3, retryTimeout = 1000, onFailed = v => v } = options
  let attempts = 0,
    result,
    success = false,
    error

  while (attempts <= retryCount && !success) {
    attempts++

    try {
      result = await action()

      success = true
    } catch (err) {
      error = err
      await Wait(retryTimeout)
    }
  }

  if (!success) {
    onFailed(error)
    return { err: error }
  }
  return { err: null, item: result }
}

function Diff(a, b, options = {}) {
  if (!b || !isObject(b)) return a

  let { strategy = 'default', arrayId = 'id', rules = [], parentKey = '' } = options

  let diffs = {}

  let getKey = (...args) => args.filter(a => ![null, undefined, false, ''].includes(a)).join('.')
  let isRelationalArray = a => a.length && a.every(i => isObject(i) && arrayId in i)
  let isArrayIdEqual = (a, b) => a[arrayId].split(':').pop() === b[arrayId].split(':').pop()
  let arraysMatch = (a, b) => a.length === b.length && Hash(a.slice()) === Hash(b.slice())

  let compare = function (previous, current, key) {
    // Get the object type
    let previousType = Object.prototype.toString.call(previous)
    let currentType = Object.prototype.toString.call(current)

    // If items are different types
    if (previousType !== currentType) return (diffs[key] = current)

    // If an object, compare recursively
    if (previousType === '[object Object]') {
      const rule = rules.find(({ onKey }) => onKey === getKey(parentKey, key))
      // If there is a rule / match -> wrap in object since Diff func requires objects -> output is never used
      const previousTarget = rule ? { [rule.compare]: getByPath(rule.compare, previous) } : previous
      const currentTarget = rule ? { [rule.compare]: getByPath(rule.compare, current) } : current

      let objDiff = Diff(previousTarget, currentTarget, { ...options, parentKey: getKey(parentKey, key) })
      if (!Object.keys(objDiff).length) return
      return (diffs[key] = rule ? current : objDiff)
    }

    // If an array, compare
    if (previousType === '[object Array]') {
      if (strategy === 'diff' && isRelationalArray(previous) && isRelationalArray(current)) {
        let deleted = previous.filter(
          previousEntry => !current.find(currentEntry => isArrayIdEqual(currentEntry, previousEntry)),
        )
        let previousRemaining = previous.filter(previousEntry =>
          current.find(currentEntry => isArrayIdEqual(currentEntry, previousEntry)),
        )

        let arrayDiff = current
          .map((currentEntry, currentIndex) => {
            let previousEntry = previousRemaining.find(previousEntry => isArrayIdEqual(previousEntry, currentEntry))
            let previousEntryIndex = previousRemaining.indexOf(previousEntry)

            if (!previousEntry) return currentEntry

            if (currentIndex !== previousEntryIndex)
              currentEntry[arrayId] = `Sort[${currentIndex}]:${currentEntry[arrayId].split(':').pop()}`

            const rule = rules.find(({ onKey }) => onKey === getKey(parentKey, key))
            // If there is a rule / match -> wrap in object since Diff func requires objects -> output is never used
            const previousTarget = rule ? { [rule.compare]: getByPath(rule.compare, previousEntry) } : previousEntry
            const currentTarget = rule ? { [rule.compare]: getByPath(rule.compare, currentEntry) } : currentEntry

            let diff = Diff(previousTarget, currentTarget, { ...options, parentKey: getKey(parentKey, key) })

            if (!Object.keys(diff).length) return
            return { [arrayId]: currentEntry[arrayId], ...(rule ? currentEntry : diff) }
          })
          .filter(Boolean)

        arrayDiff.push(...deleted.map(e => ({ [arrayId]: `Delete:${e[arrayId]}` })))

        if (arrayDiff.length) diffs[key] = arrayDiff
        return
      }
      if (!arraysMatch(previous, current)) diffs[key] = current
      return
    }

    // Else if it's a function, convert to a string and compare
    if (previousType === '[object Function]' && previous.toString() !== current.toString()) diffs[key] = current

    // Otherwise, just compare
    if (previous !== current) diffs[key] = current
  }

  // Loop through the first object
  for (let key in a) {
    if (!a.hasOwnProperty(key)) continue
    compare(a[key], b[key], key)
  }

  // Loop through the second object and find missing items
  for (let key in b) {
    if (!b.hasOwnProperty(key)) continue
    if (!a[key] && a[key] !== b[key]) {
      diffs[key] = b[key]
    }
  }

  // Return the object of differences
  return diffs
}

function hasDiff(...args) {
  return Boolean(Object.keys(Diff(...args)).length)
}

function PrefixObjectKeys(prefix, obj) {
  return Object.fromEntries(Object.entries(obj).map(([key, value]) => [prefix + key, value]))
}

function ExtractString(str, pattern, options = {}) {
  let { group = 1 } = options
  let match = str.match(pattern)
  if (!match) return
  return match[group]
}

function SimpleMarkdownToHtml(str) {
  str = str.replace(/-{2}(.+?)-{2}/g, '<span class="text--small">$1</span>')
  str = str.replace(/\*{2}(.+?)\*{2}/g, '<b>$1</b>')
  str = str.replace(/\*{1}(.+?)\*{1}/g, '<i>$1</i>')
  str = str.replace(/\[(.+)\]\((.+)\)/g, '<a href="$2" target="_blank">$1</a>')
  return str
}

function EscapeRegex(string) {
  return string.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')
}

function Slugify(str) {
  str = str.replace(/^\s+|\s+$/g, '') // trim
  str = str.toLowerCase()

  // remove accents, swap ñ for n, etc
  const replaceTable = {
    å: 'a',
    æ: 'ae',
    à: 'a',
    á: 'a',
    ä: 'a',
    â: 'a',
    è: 'e',
    é: 'e',
    ë: 'e',
    ê: 'e',
    ì: 'i',
    í: 'i',
    ï: 'i',
    î: 'i',
    ò: 'o',
    ó: 'o',
    ö: 'o',
    ô: 'o',
    ø: 'o',
    ù: 'u',
    ú: 'u',
    ü: 'u',
    û: 'u',
    ñ: 'n',
    ç: 'c',
    '·': '-',
    '/': '-',
    _: '-',
    ',': '-',
    ':': '-',
    ';': '-',
  }

  Object.entries(replaceTable).forEach(([from, to]) => (str = str.replace(new RegExp(from, 'g'), to)))

  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
    .replace(/\s+/g, '-') // collapse whitespace and replace by -
    .replace(/-+/g, '-') // collapse dashes

  return str
}

function CopyToClipboard(str) {
  const el = document.createElement('textarea')
  el.value = str
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
}

class Deferred {
  constructor() {
    this.promise = new Promise((resolve, reject) => {
      this.reject = reject
      this.resolve = resolve
    })
  }
}

function getSecureRandomString(length, charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789$&?') {
  let result = ''

  if (window.crypto && window.crypto.getRandomValues) {
    let values = new Uint32Array(length)
    window.crypto.getRandomValues(values)
    for (let i = 0; i < length; i++) {
      result += charset[values[i] % charset.length]
    }
    return result
  } else throw new Error("Your browser can't generate secure random numbers")
}

function ToArray(str) {
  if (str instanceof Array) return str
  if (![null, undefined].includes(str)) return [str]
  return []
}

function DownloadUrl(url, name) {
  fetch(url, { headers: { 'Cache-Control': 'no-cache' } })
    .then(res => res.blob())
    .then(blob => {
      const blobURL = URL.createObjectURL(blob)
      return trigger(blobURL, name)
    })
    .catch(err => {
      return trigger(url, name, '_blank')
    })

  let trigger = (href, name, target) => {
    const a = document.createElement('a')
    a.href = href
    a.style = 'display: none'
    if (target) a.target = target
    if (name) a.download = name

    document.body.appendChild(a)
    a.click()

    document.body.removeChild(a)
  }
}

function StripHTML(val) {
  return (val || '').replace(/<[^>]*>?/gm, '')
}

function StripSimpleMarkdown(str) {
  str = str.replace(/--(.+?)--/g, '$1') // Replace double dashes
  str = str.replace(/\*{2}(.+?)\*{2}/g, '$1') // Replace double asterisks
  str = str.replace(/\*(.+?)\*/g, '$1') // Replace single asterisks
  str = str.replace(/\[(.+?)\]\((.+?)\)/g, '$1 ($2)') // Replace links
  return str
}

function ObjectToDotNotation(obj, current) {
  let res = {}

  for (let key in obj) {
    let value = obj[key]
    let newKey = current ? current + '.' + key : key
    if (isObject(value)) {
      res = { ...res, ...ObjectToDotNotation(value, newKey) }
    } else {
      res[newKey] = value
    }
  }

  return res
}

function mergeAllDeepRight(...args) {
  const [a, b, ...rest] = args
  let result = mergeDeepRight(a, b)
  return rest.length ? mergeAllDeepRight(result, ...rest) : result
}

function getCustomSteps(steps, resource, options = {}) {
  //steps = [{resource: page, step: 'basis', location: 'key:after/before}]

  if (!steps || !steps.length) return []

  const { overwriteFrom = [], item = {}, customStepComponent = StepCustomField } = options

  const data = new Set()
  const unique = steps.filter(item => {
    if (data.has(item.step)) return false
    data.add(item.step)
    return true
  })

  return [...unique]
    .map(({ step, location }, index) => {
      const customFieldsPlacement = [`${resource}:${step}`, step]

      //check if current fields has condtion
      const currentFields = (item.customFieldsConfig || []).flatMap(({ schema = [] }) =>
        schema.filter(({ placement }) => {
          if (!placement) return
          return placement.step === step && placement.resource === resource
        }),
      )

      if (
        !overwriteFrom.length &&
        currentFields.length &&
        !(currentFields || []).some(i => {
          const condition = path(['options', 'condition'], i)
          if (!condition) return true
          return isObject(condition) ? new Query(condition).test(item) : condition
        })
      )
        return

      const id = Slugify(typeof step === 'string' ? step : step.id)
      const overwriteStep = overwriteFrom.find(i => i.id === id)

      if (overwriteStep) return overwriteStep

      return {
        location,
        order: 100 + index,
        id,
        rawId: step,
        label: i18n.tc(step),
        component: customStepComponent,
        props: {
          customFieldsPlacement,
        },
      }
    })
    .filter(Boolean)
}

function SortByLocation(items) {
  return items.reduce(
    (result, field) => {
      if (path(['location'], field)) {
        const [block, placement] = field.location.split(':')

        const blockIndex = result.findIndex(i => i.id === block)

        if (~blockIndex) {
          const index = result.findIndex(i => i.id === field.id)
          result.splice(index, 1) // delete item

          let newIndex = blockIndex
          if (placement === 'after') {
            newIndex = blockIndex + (blockIndex < index ? 1 : 0)
          } else if (placement === 'before') {
            newIndex = blockIndex + (blockIndex < index ? 0 : -1)
            if (newIndex < 0) newIndex = 0
          }

          // Insert field at new position
          result.splice(newIndex, 0, field)
        }
      }

      return result
    },
    [...items],
  )
}

function BytesToSize(bytes) {
  let sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes == 0) return '0 Byte'
  let i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)))
  return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i]
}

function buildStepsFields(fields, customFieldSchemas = [], ctx) {
  return fields
    .map(i => i(ctx))
    .filter(item => !('condition' in item) || item.condition)
    .reduce(
      (acc, curr) => {
        const key = curr.key.replaceAll('.', '-')

        const isSlot = customFieldSchemas.find(c => {
          if (Array.isArray(c.component)) {
            return c.component.find(component => component.key === key && component.component === 'Slot')
          }
          return (
            c.component.split(',').some(part => {
              const [comp, partKey] = part.split(':')
              return comp === 'Slot' && partKey === key
            }) ||
            (c.component === 'Slot' && c.key === key)
          )
        })

        if (isSlot) {
          acc.slots.push({ ...curr, slotKey: key, props: { ...curr.props, isSlot: true } })
        } else {
          acc.default.push(curr)
        }

        return acc
      },
      {
        slots: [],
        default: [],
      },
    )
}

function isNumeric(str) {
  if (typeof str === 'number') return true
  if (typeof str != 'string') return false
  return !isNaN(str) && !isNaN(parseFloat(str))
}

function dotNotationToObject(obj) {
  return Object.entries(obj).reduce((res, [k, v]) => {
    return assocPath(k.split('.'), v, res)
  }, {})
}

function LoadScript(src, options = { type: 'text/javascript' }) {
  if (!document) return

  if (document.querySelector(`script[src="${src}"]`)) return

  let { type } = options

  let script = document.createElement('script')
  script.type = type
  script.src = src

  document.body.appendChild(script)
}

function GetAttachmentIcon(type = '') {
  const patterns = [
    [/^image\//, 'file-image'],
    [/^video\//, 'file-video'],
    [/^audio\//, 'file-audio'],
    [/pdf$/, 'file-pdf'],
    [/spreadsheet|excel|csv/, 'file-excel'],
    [/presentation|powerpoint/, 'file-powerpoint'],
    [/word|document\./, 'file-word'],
    [/(zip|rar|7z|gzip|tar)/, 'file-zipper'],
    [/(json|xml|html|css|javascript|code)/, 'file-code'],
    [/text\//, 'file-lines'],
  ]

  for (const [pattern, icon] of patterns) {
    if (pattern.test(type)) return icon
  }

  return 'file'
}

function HexToHsl(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255
  let g = parseInt(hex.slice(3, 5), 16) / 255
  let b = parseInt(hex.slice(5, 7), 16) / 255

  let max = Math.max(r, g, b)
  let min = Math.min(r, g, b)
  let h,
    s,
    l = (max + min) / 2

  if (max === min) {
    h = s = 0
  } else {
    let d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  }
}

function SimpleTemplateEngine(str, data, options = {}) {
  const { strict = false, filters = {} } = options

  const missing = []

  const result = str.replace(/\{([^}]+)\}/g, function (match, path) {
    let [key, filter] = path.split('|')
    let keys = key.split('.')

    let value = data

    for (let i = 0; i < keys.length; i++) {
      if (value == null) {
        missing.push(match)
        return match
      }
      value = value[keys[i]]
    }

    if (value == null) {
      missing.push(match)
      return match
    }

    return filters[filter] ? filters[filter](value) : value
  })

  return strict && missing.length ? null : result
}

export {
  GetAttachmentIcon,
  SimpleTemplateEngine,
  mergeAllDeepRight,
  StripHTML,
  StripSimpleMarkdown,
  CopyToClipboard,
  ToArray,
  Slugify,
  EscapeRegex,
  Deferred,
  PrefixObjectKeys,
  RemoveByValue,
  ResidentialNumberPatternParser,
  Clone,
  PartialUpdate,
  IconBinding,
  setByPath,
  getByPath,
  Debounce,
  isObject,
  getDimensions,
  getCookie,
  Capitalize,
  ValueComp,
  Initials,
  CamelCase,
  Wait,
  SortByKey,
  WaitUntil,
  Run,
  Diff,
  hasDiff,
  ExtractString,
  ReduceArrayUniqueByKey,
  getSecureRandomString,
  DownloadUrl,
  ObjectToDotNotation,
  SimpleMarkdownToHtml,
  getCustomSteps,
  SortByLocation,
  BytesToSize,
  buildStepsFields,
  isNumeric,
  dotNotationToObject,
  LoadScript,
  HexToHsl,
  LoadImage,
  getButtonThemes,
  getModelTemplateThemeItem,
}
